/**
 * GA4 Event Tracking Utility
 * Full conversion funnel + engagement tracking.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export function trackEvent(eventName: string, params?: Record<string, string | number | boolean>) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, params);
  }
}

// ── Funnel tracking state ──
let funnelState = {
  pageLoaded: false,
  scrolledToTool: false,
  interactedWithTool: false,
  summaryGenerated: false,
  resultActioned: false, // copy/download/share
};

function trackFunnelStep(step: string) {
  trackEvent("funnel_step", { step, page: window.location.pathname });
}

// ── Pre-defined events ──
export const analytics = {
  // Funnel step 1: Page view (auto via GA4)

  // Funnel step 2: Scroll to tool
  scrollToTool: () => {
    if (!funnelState.scrolledToTool) {
      funnelState.scrolledToTool = true;
      trackFunnelStep("scroll_to_tool");
    }
  },

  // Funnel step 3: First interaction with tool (click any option)
  toolInteraction: (element: string) => {
    if (!funnelState.interactedWithTool) {
      funnelState.interactedWithTool = true;
      trackFunnelStep("tool_first_interaction");
    }
    trackEvent("tool_interaction", { element });
  },

  // Funnel step 4: Submit summarization
  clickSummarize: (mode: string, tone: string, length: string, format: string) => {
    trackFunnelStep("submit_summarize");
    funnelState.summaryGenerated = true;
    trackEvent("click_summarize", { mode, tone, length, format });
  },

  // Funnel step 5: Result actions
  copyResult: (tool: string) => {
    if (!funnelState.resultActioned) {
      funnelState.resultActioned = true;
      trackFunnelStep("result_actioned");
    }
    trackEvent("copy_result", { tool });
  },

  downloadResult: (tool: string, format: string) => {
    if (!funnelState.resultActioned) {
      funnelState.resultActioned = true;
      trackFunnelStep("result_actioned");
    }
    trackEvent("download_result", { tool, format });
  },

  shareResult: (tool: string, method: string) => {
    if (!funnelState.resultActioned) {
      funnelState.resultActioned = true;
      trackFunnelStep("result_actioned");
    }
    trackEvent("share_result", { tool, method });
  },

  generateShareLink: (tool: string) => {
    trackEvent("generate_share_link", { tool });
  },

  // ── Engagement events ──
  clickShorten: (level: string) =>
    trackEvent("click_shorten", { level }),
  clickGrammarCheck: () =>
    trackEvent("click_grammar_check"),
  clickRewrite: (style: string) =>
    trackEvent("click_rewrite", { style }),
  toolSwitch: (from: string, to: string) =>
    trackEvent("tool_switch", { from_tool: from, to_tool: to }),
  scrollDepth: (percentage: number, page: string) =>
    trackEvent("scroll_depth", { percentage, page }),
  selectMode: (mode: string) =>
    trackEvent("select_mode", { mode }),

  // CTA clicks
  clickHeroCTA: () =>
    trackEvent("cta_click", { location: "hero", label: "summarize_now" }),
  clickFloatingCTA: () =>
    trackEvent("cta_click", { location: "floating", label: "summarize_now" }),
  clickFinalCTA: () =>
    trackEvent("cta_click", { location: "final_section", label: "summarize_now" }),
  clickNicheCTA: (slug: string) =>
    trackEvent("cta_click", { location: "niche_card", label: slug }),

  // Content engagement
  newsletterSignup: () =>
    trackEvent("newsletter_signup"),
  faqExpand: (question: string) =>
    trackEvent("faq_expand", { question: question.substring(0, 80) }),
  blogClick: (slug: string) =>
    trackEvent("blog_click", { slug }),

  // Timing
  timeToFirstSummary: (seconds: number) =>
    trackEvent("time_to_first_summary", { seconds }),

  // Session quality
  engagedSession: (sections_viewed: number, time_on_page: number) =>
    trackEvent("engaged_session", { sections_viewed, time_on_page }),
};

// ── Auto-track scroll depth milestones ──
let trackedMilestones = new Set<number>();

function setupScrollTracking() {
  const milestones = [25, 50, 75, 90];
  const page = window.location.pathname;

  const handleScroll = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );

    for (const m of milestones) {
      if (scrollPercent >= m && !trackedMilestones.has(m)) {
        trackedMilestones.add(m);
        analytics.scrollDepth(m, page);
      }
    }

    // Track scroll to tool area (approx 300-600px)
    if (window.scrollY > 200) {
      analytics.scrollToTool();
    }
  };

  window.addEventListener("scroll", handleScroll, { passive: true });
}

// ── Auto-track engaged sessions ──
function setupEngagementTracking() {
  const startTime = Date.now();
  let sectionsViewed = 0;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) sectionsViewed++;
      });
    },
    { threshold: 0.3 }
  );

  // Observe all sections after DOM is ready
  requestAnimationFrame(() => {
    document.querySelectorAll("main > section, main > div > section").forEach((section) => {
      observer.observe(section);
    });
  });

  // Send engaged session on page unload
  const sendEngagement = () => {
    const timeOnPage = Math.round((Date.now() - startTime) / 1000);
    if (timeOnPage > 10 && sectionsViewed > 2) {
      analytics.engagedSession(sectionsViewed, timeOnPage);
    }
  };

  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") sendEngagement();
  });
}

// ── Initialize ──
export function initAnalytics() {
  if (typeof window === "undefined") return;
  funnelState = {
    pageLoaded: true,
    scrolledToTool: false,
    interactedWithTool: false,
    summaryGenerated: false,
    resultActioned: false,
  };
  trackedMilestones = new Set();
  setupScrollTracking();
  setupEngagementTracking();
}
